import React, { useContext, useEffect, useState, useCallback } from "react";
import {
  Card,
  Button,
  Modal,
  TextInput,
  Label,
  Table,
  Select,
  Dropdown,
  FileInput,
} from "flowbite-react";
import { Store } from "../context/store";
import { useNavigate } from "react-router-dom";
import apiRequest from "../utils/apiRequest";
import { HiOutlineExclamationCircle, HiPencilAlt } from "react-icons/hi";
import CustomToast from "../components/Toast";
import { IoMdAddCircleOutline, IoMdArrowBack } from "react-icons/io";
import { FiFilter } from "react-icons/fi";
import { HiUpload } from "react-icons/hi";
import { useAllPermissions } from "../utils/usePermission";
import DashboardLayout from "../components/layout/DashboardLayout";

function Budget() {
  const store = useContext(Store);
  const [baseUrl] = store.url;
  const [msg, setMsg] = useState("");
  const [toastMsg, setToastMsg] = useState("");
  const [toastType, setToastType] = useState("");
  const [toastOpen, setToastOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openModalOut, setOpenModalOut] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [secondCard, setSecondCard] = useState(false);
  const navigate = useNavigate();

  const [budget, setBudget] = useState([]);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [form, setForm] = useState({
    title: "",
    amount: "",
    category: "",
  });
  const [openExcelUploadModal, setOpenExcelUploadModal] = useState(false);
  const [excelFile, setExcelFile] = useState(null);
  const [excelLoading, setExcelLoading] = useState(false);

  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - 2025 + 2 },
    (_, i) => 2025 + i,
  );

  const { canWriteSettings } = useAllPermissions();

  const categories = ["Operating expenses", "Admin"];

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // NEW: Handle Excel file selection
  const handleFileChange = (e) => {
    setExcelFile(e.target.files[0]);
  };

  const handleEdit = (item) => {
    setForm({
      title: item.title,
      amount: item.amount,
      category: item.category || "",
    });
    setIsEditing(true);
    setEditingId(item.id);
    setSecondCard(true);
  };

  const loadBudget = useCallback(async () => {
    try {
      const token = localStorage.getItem("royal-token");
      const url = `${baseUrl}/budget-expenses?year=${selectedYear}`;
      const { data } = await apiRequest(url, "GET", null, token);
      setBudget(data);
    } catch (error) {
      handleAuthError(error);
    }
  }, [baseUrl, selectedYear]);

  useEffect(() => {
    loadBudget();
  }, [loadBudget]);

  const handleAuthError = (error) => {
    const unauthorizedErrors = [
      "Access Denied: Invalid or expired token. Please login again",
      "Access Denied: Please log in.",
      "Access Denied: Invalid Token.",
      "Access Denied: Invalid or expired token.",
      "Access Denied: Invalid or Expired Token.",
      "Access Denied: No token provided.",
    ];
    if (unauthorizedErrors.includes(error.message)) {
      setMsg("Session Expired!");
      setOpenModalOut(true);
      localStorage.removeItem("royal-token");
    } else {
      console.log(error);
    }
  };

  const handleSubmit = async () => {
    if (!form.title || !form.amount) {
      setToastMsg("Title, Amount and Year are required.");
      setToastOpen(true);
      setToastType("error");
      setTimeout(() => {
        setToastOpen(false);
        setToastType("");
      }, 5000);
      return;
    }

    setLoading(true);

    const token = localStorage.getItem("royal-token");
    const url = isEditing
      ? `${baseUrl}/budget-expense/${editingId}`
      : `${baseUrl}/budget-expense`;
    const method = isEditing ? "PUT" : "POST";

    try {
      await apiRequest(url, method, { ...form, year: selectedYear }, token);
      setForm({ title: "", amount: "", category: "" });
      setToastMsg(
        isEditing
          ? "Budget expense updated successfully!"
          : "Budget expense created successfully!",
      );
      setToastOpen(true);
      loadBudget();
      setIsEditing(false);
      setEditingId(null);
      setSecondCard(true);
      setToastType("success");
      setTimeout(() => {
        setToastOpen(false);
        setToastType("");
        setToastMsg("");
      }, 5000);
    } catch (error) {
      handleAuthError(error);
      setToastOpen(true);
      setToastType("error");
      setToastMsg(error.message || "An error occurred. Please try again.");
      setTimeout(() => {
        setToastOpen(false);
        setToastType("");
        setToastMsg("");
      }, 5000);
    } finally {
      setLoading(false);
    }
  };

  // NEW: Handle Excel file submission
  const handleSubmitExcelUpload = async () => {
    if (!excelFile) {
      setToastMsg("Please select an Excel file to upload.");
      setToastOpen(true);
      setToastType("error");
      setTimeout(() => {
        setToastOpen(false);
        setToastType("");
      }, 5000);
      return;
    }

    setExcelLoading(true); // Set loading for Excel upload

    const token = localStorage.getItem("royal-token");
    const url = `${baseUrl}/budget-expense-upload`; // Your new API endpoint
    const formData = new FormData();
    formData.append("file", excelFile);
    formData.append("category", form.category); // Include category from the form
    formData.append("year", selectedYear); // Include selectedYear

    try {
      const { message } = await apiRequest(url, "POST", formData, token);

      setToastMsg(message);
      setToastOpen(true);
      setToastType("success");
      setOpenExcelUploadModal(false); // Close modal on success
      setExcelFile(null); // Clear selected file
      loadBudget(); // Reload budget data
      setTimeout(() => {
        setToastOpen(false);
        setToastType("");
        setToastMsg("");
      }, 5000);
    } catch (error) {
      handleAuthError(error); // Re-use auth error handler
      setToastOpen(true);
      setToastType("error");
      setToastMsg(
        error.message ||
          "Error uploading Excel file. Please check the file format and try again.",
      );
      setTimeout(() => {
        setToastOpen(false);
        setToastType("");
        setToastMsg("");
      }, 5000);
    } finally {
      setExcelLoading(false);
      setForm({ title: "", amount: "", category: "" });
    }
  };

  const logOut = () => {
    navigate("/");
  };

  return (
    <>
      <title>Budget Expense</title>
      <meta
        name="description"
        content="Create and view budget expenses for Royalmines Property Office"
      />
      <DashboardLayout active="budget" animation="fade-up">
            <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
              Budget Expenses
            </h5>
            {!secondCard && (
              <div className="flex flex-wrap gap-2 mt-5 justify-end">
                <Button
                  color="success"
                  onClick={() => setSecondCard(true)}
                  className="w-full sm:w-auto"
                  disabled={!canWriteSettings}
                >
                  <IoMdAddCircleOutline className="me-2" />
                  New Budget Expense
                </Button>
                <Button
                  color="blue"
                  onClick={() => setOpenExcelUploadModal(true)}
                  className="w-full sm:w-auto"
                  disabled={!canWriteSettings}
                >
                  <HiUpload className="me-2" />
                  Upload from Excel
                </Button>
                <Dropdown
                  label={
                    <Button className="bg-[#6eb5ff] w-full sm:w-auto">
                      <FiFilter className="me-2" /> Filter Year
                    </Button>
                  }
                  dismissOnClick={true}
                  inline
                >
                  {years.map((year) => (
                    <Dropdown.Item
                      key={year}
                      className={selectedYear === year ? "active-dropdown" : ""}
                      onClick={() => {
                        setSelectedYear(year);
                      }}
                    >
                      {year}
                    </Dropdown.Item>
                  ))}
                </Dropdown>
              </div>
            )}

            {secondCard && (
              <Card className="rounded-xl my-4">
                <Button
                  onClick={() => {
                    setSecondCard(false);
                    setForm({ title: "", amount: "", category: "" });
                  }}
                  className="bg-[#6eb5ff] w-full sm:w-1/2 md:w-[33%] mb-4 float-left"
                >
                  <IoMdArrowBack className="me-2" />
                  Go Back
                </Button>
                <div className="flex justify-center items-center">
                  <Card className="w-full sm:w-3/4 md:w-1/2 card">
                    <div className="flex flex-col gap-4">
                      <div>
                        <Label htmlFor="title" value="Title" />
                        <TextInput
                          id="title"
                          name="title"
                          placeholder="e.g. Maintenance"
                          value={form.title}
                          onChange={handleChange}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Label htmlFor="amount" value="Amount" />
                        <TextInput
                          id="amount"
                          name="amount"
                          type="number"
                          step="0.01"
                          placeholder="e.g. 150000"
                          value={form.amount}
                          onChange={handleChange}
                          className="w-full"
                        />
                      </div>
                      <div>
                        <Label htmlFor="category" value="Category (Optional)" />
                        <Select
                          id="category"
                          name="category"
                          value={form.category}
                          onChange={handleChange}
                          className="w-full"
                        >
                          <option value="">-- Select Category --</option>
                          {categories.map((cat) => (
                            <option key={cat} value={cat}>
                              {cat}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="year" value="Year" />
                        <Select
                          id="year"
                          name="year"
                          value={selectedYear}
                          onChange={(e) =>
                            setSelectedYear(parseInt(e.target.value))
                          }
                          className="w-full"
                        >
                          {years.map((year) => (
                            <option key={year} value={year}>
                              {year}
                            </option>
                          ))}
                        </Select>
                      </div>
                      <Button
                        onClick={handleSubmit}
                        disabled={loading}
                        className="w-full"
                      >
                        {loading ? "Submitting..." : "Submit"}
                      </Button>
                      {isEditing && (
                        <Button
                          color="gray"
                          onClick={() => {
                            setForm({ title: "", amount: "", category: "" });
                            setIsEditing(false);
                            setEditingId(null);
                            setSecondCard(false);
                          }}
                          className="w-full"
                        >
                          Cancel Edit
                        </Button>
                      )}
                    </div>
                  </Card>
                </div>
              </Card>
            )}

            {!secondCard && (
              <Card className="rounded-xl my-4">
                <div>
                  <div className="flex justify-between items-center mb-4">
                    <h5 className="text-xl font-bold leading-none text-gray-900 dark:text-white">
                      <span>Budget Expenses ({selectedYear})</span>
                    </h5>
                  </div>
                </div>
                {budget.length > 0 ? (
                  <div className="overflow-x-auto overflow-y-scroll h-[50vh] scrollbar-always-visible">
                    <Table
                      striped
                      className="w-full text-sm text-left text-gray-500"
                    >
                      <thead className="bg-gray-100 sticky top-0 z-10">
                        <tr>
                          <th className="px-6 py-3">#</th>
                          <th className="px-6 py-3">Title</th>
                          <th className="px-6 py-3">Category</th>
                          <th className="px-6 py-3">Amount</th>
                          <th className="px-6 py-3">Balance</th>
                          <th className="px-6 py-3">Date</th>
                          <th className="px-6 py-3">Action</th>
                        </tr>
                      </thead>
                      <Table.Body className="divide-y">
                        {budget.map((item, i) => (
                          <Table.Row key={item._id}>
                            <Table.Cell>{i + 1}</Table.Cell>
                            <Table.Cell>{item.title}</Table.Cell>
                            <Table.Cell>{item.category || "—"}</Table.Cell>
                            <Table.Cell>
                              &#8358; {Number(item.amount)?.toLocaleString()}
                            </Table.Cell>
                            <Table.Cell>
                              &#8358;{" "}
                              {Number(item.amountLeft)?.toLocaleString()}
                            </Table.Cell>
                            <Table.Cell>
                              {new Date(item.createdAt).toLocaleDateString()}
                            </Table.Cell>
                            <Table.Cell>
                              <Button
                                size="xs"
                                color="gray"
                                onClick={() => handleEdit(item)}
                                className="w-full"
                                disabled={!canWriteSettings}
                              >
                                <HiPencilAlt className="mr-1" />
                                Edit
                              </Button>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 py-4">
                    No budget entries found for {selectedYear}.
                  </p>
                )}
              </Card>
            )}
      </DashboardLayout>

      {/* NEW: Excel Upload Modal */}
      <Modal
        show={openExcelUploadModal}
        onClose={() => {
          setOpenExcelUploadModal(false);
          setExcelFile(null); // Clear selected file when closing modal
          setForm((prev) => ({ ...prev, category: "" })); // Clear category for excel upload
        }}
        popup
      >
        <Modal.Header>Upload Budget from Excel</Modal.Header>
        <Modal.Body>
          <div className="space-y-6">
            <div>
              <Label
                htmlFor="excelFile"
                value="Select Excel File (.xls, .xlsx)"
              />
              <FileInput
                id="excelFile"
                name="file"
                accept=".xls,.xlsx"
                onChange={handleFileChange}
              />
              {excelFile && (
                <p className="text-sm text-gray-500 mt-2">
                  Selected: {excelFile.name}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="excelCategory" value="Category (Optional)" />
              <Select
                id="excelCategory"
                name="category" // Use 'category' to reuse form state logic
                value={form.category}
                onChange={handleChange}
              >
                <option value="">-- Select Category --</option>
                {categories.map((cat) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="excelYear" value="Year (Required)" />
              <Select
                id="excelYear"
                name="year"
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value))}
              >
                {years.map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </Select>
            </div>
            <Button onClick={handleSubmitExcelUpload} disabled={excelLoading}>
              {excelLoading ? "Uploading..." : "Upload & Process"}
            </Button>
            <p className="text-sm text-gray-500 mt-4">
              * The Excel file should have 'Title' in the first column and
              'Amount' in the second column.
            </p>
          </div>
        </Modal.Body>
      </Modal>

      {/* Toast */}
      {toastOpen && (
        <CustomToast
          message={toastMsg}
          type={toastType}
          duration={5000}
          onClose={() => setToastOpen(false)}
        />
      )}

      {/* Logout Modal */}
      <Modal show={openModalOut} size="md" popup>
        <Modal.Header />
        <Modal.Body>
          <div className="text-center">
            <HiOutlineExclamationCircle className="mx-auto mb-4 h-14 w-14 text-gray-400 dark:text-gray-200" />
            <h3 className="mb-5 text-lg font-normal text-gray-500 dark:text-gray-400">
              {msg}
            </h3>
            <div className="flex justify-center gap-4">
              <Button color="failure" onClick={logOut}>
                OK
              </Button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
}

export default Budget;
